<template>
    <div class="matchDetailWrapper notranslate">
        <div class="matchDetailNavbar">
            <div class="matchDetailInfo">
                <div class="eventHeader">
                    <div v-if="event?.isLive && isShowCountdown" class="matchesTime">
                        <SboIcon
                            name="system:timer"
                            v-bind:size="14"
                            class="countdown"
                        />
                        <div class="countdown-text">
                            <span
                                v-if="isShowCountdownHours"
                            >{{ countdownDisplay.hours }}:</span>
                            <span>{{ countdownDisplay.minutes }}</span><span>:</span><span class="num">{{ countdownDisplay.seconds }}</span>
                        </div>
                    </div>
                    <div v-else-if="liveTimeDisplay" class="matchesTime">
                        <div class="redDot" />
                        <div class="time">
                            {{ liveTimeDisplay }}
                            <span v-if="event && isShowInjuryTime(event)">
                                +{{ event.extraInfo?.injuryTime }}'
                            </span>
                        </div>
                    </div>
                    <div v-else-if="showTimeDisplay" class="matchesTime">
                        {{ i18n.t('upcoming') }}: {{ showTimeDisplay }}
                    </div>
                    <div class="leagueName">
                        {{ leagueName }}
                        <SvgIcon
                            v-if="leagueInfo"
                            v-tooltip="leagueInfo"
                            name="icon-system-cycle-info"
                            right
                        />
                    </div>
                    <div class="eventItems">
                        <StatisticsButton
                            v-bind:event="event"
                            flat
                        />
                        <NavbarDivider />
                        <VButton
                            v-tooltip="i18n.t('live_stream_icon_tooltip')"
                            v-gtm:@click
                            v-gtm:label="'LiveStream'"
                            flat
                            v-bind:active="matchDetailPreview === MatchDetailPreview.LiveStream"
                            v-bind:disabled="!isLiveStreamPlayable"
                            v-bind:activeUnderline="matchDetailPreview === MatchDetailPreview.LiveStream"
                            v-on:click="toggleMatchDetailPreview(MatchDetailPreview.LiveStream)"
                        >
                            <SvgIcon name="icon-system-tvPlay" size="xl" />
                        </VButton>
                        <VButton
                            v-tooltip="i18n.t('live_court_icon_tooltip')"
                            v-gtm:@click
                            v-gtm:label="'LiveCourt'"
                            flat
                            v-bind:active="matchDetailPreview === MatchDetailPreview.LiveCourt"
                            v-bind:disabled="!isLiveCourtAvailable"
                            v-bind:activeUnderline="matchDetailPreview === MatchDetailPreview.LiveCourt"
                            v-on:click="toggleMatchDetailPreview(MatchDetailPreview.LiveCourt)"
                        >
                            <SvgIcon name="icon-system-courtFootball" size="xl" />
                        </VButton>
                    </div>
                </div>
                <div class="eventInfo">
                    <div v-if="event && isShowJersey" class="flag">
                        <div class="flag-item">
                            <Jersey v-bind:jersey="event.homeTeam.jersey" />
                        </div>
                        <div class="flag-item">
                            <Jersey v-bind:jersey="event.awayTeam.jersey" />
                        </div>
                    </div>
                    <template v-if="event?.isLive">
                        <template v-for="item in liveScoreItems" v-bind:key="item.name">
                            <div v-if="item.isShow" class="teamLiveScore">
                                <div v-bind:class="item.isSetScore ? 'setScore' : 'liveScore'">
                                    {{ item.homeScore }}
                                </div>
                                <div v-bind:class="item.isSetScore ? 'setScore' : 'liveScore'">
                                    {{ item.awayScore }}
                                </div>
                            </div>
                        </template>
                    </template>
                    <div class="teamName">
                        <div class="teamName-item">
                            {{ homeTeamName }}
                        </div>
                        <div class="teamName-item">
                            {{ awayTeamName }}
                        </div>
                    </div>
                    <div class="matchFoulCard">
                        <div class="foulCardGroup">
                            <FoulCard v-bind:count="event?.homeTeam.redCard ?? 0" />
                        </div>
                        <div class="foulCardGroup">
                            <FoulCard v-bind:count="event?.awayTeam.redCard ?? 0" />
                        </div>
                    </div>
                </div>
            </div>

            <Preview
                v-if="matchDetailPreview === MatchDetailPreview.LiveStream"
                v-gtm:@click
                v-gtm:label="'LiveStream_Expand'"
                v-on:click="openLiveStream"
            >
                <component
                    v-bind:is="streamDisplay.component"
                    v-if="streamDisplay"
                    v-bind:key="streamDisplay.key"
                    v-bind:videoUrl="streamDisplay.props.videoUrl"
                    v-bind:betRadar="streamDisplay.props.betRadar!"
                    v-bind:gLive="streamDisplay.props.gLive!"
                    v-bind:betConstruct="streamDisplay.props.betConstruct"
                    v-bind:betGenius="streamDisplay.props.betGenius"
                    v-bind:isEmbedOnMainPage="true"
                />
            </Preview>
            <Preview v-else-if="matchDetailPreview === MatchDetailPreview.LiveCourt" v-on:click="openLiveCourt">
                <LiveCourtPreview v-if="displayPreviewEvent" v-bind:liveCourtMatchId="displayPreviewEvent.liveCourtMatchId" />
            </Preview>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, ref, toRefs, watch } from 'vue';
    import { useRoute } from 'vue-router';
    import Jersey from '@/components/common/Jersey.vue';
    import Preview from '@/components/common/Preview.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import SvgIcon from '@/components/common/SvgIcon.vue';
    import VButton from '@/components/common/button/VButton.vue';
    import FoulCard from '@/components/common/foulCard/FoulCard.vue';
    import NavbarDivider from '@/components/common/navbar/NavbarDivider.vue';
    import LiveCourtPreview from '@/components/liveCourt/LiveCourtPreview.vue';
    import StatisticsButton from '@/components/oddsDisplay/functions/StatisticsButton.vue';
    import { isShowInjuryTime } from '@/components/oddsDisplay/matchDisplay/useMatchDisplay';
    import { useLiveStream } from '@/composable/liveStream/useLiveStream';
    import { once } from '@/composable/once';
    import { useEPenaltyEventCountdown } from '@/composable/useEPenaltyEventCountdown';
    import { useEuroRoute } from '@/composable/useEuroRoute';
    import { useJersey } from '@/composable/useJersey';
    import { useLiveCourt } from '@/composable/useLiveCourt';
    import { useLiveScoreDisplay } from '@/composable/useLiveScoreDisplay';
    import { useLiveScoreRestriction } from '@/composable/useLiveScoreRestriction';
    import { useShowTimeDisplay } from '@/composable/useShowTimeDisplay';
    import { useState } from '@/composable/useState';
    import { i18n } from '@/core/lib/i18n';
    import { getTranslated } from '@/core/lib/translationHelper';
    import { useLiveScoreEuro } from '@/core/oddsApi/composable/useLiveScore.euro';
    import type { IEventEuro } from '@/interface/IEvent';
    import type { Score } from '@/interface/ILiveScore';

    import { LiveStreamProvider, MatchDetailPreview, SiteStyle } from '@/interface/enum';

    const props = defineProps<{
        event: IEventEuro | null;
    }>();

    const { event } = toRefs(props);

    const { leagueName: _leagueName, eventName } = useEuroRoute();
    const leagueName = computed(() => {
        if (!event.value) {
            return _leagueName;
        }
        return getTranslated(event.value.league.name);
    });

    const leagueInfo = computed(() => getTranslated(event.value?.league.info));

    const homeTeamName = computed(() => {
        if (!event.value) {
            return eventName.value.split(' v ')[0];
        }
        return getTranslated(event.value.homeTeam.name);
    });

    const awayTeamName = computed(() => {
        if (!event.value) {
            return eventName.value.split(' v ')[1];
        }
        return getTranslated(event.value.awayTeam.name);
    });

    const eventId = computed(() => event.value?.id ?? 0);
    const { isSupported: isEnableLiveScore } = useLiveScoreRestriction(event);
    const { liveScore: providerLiveScore } = useLiveScoreEuro(eventId, isEnableLiveScore);

    const sboLiveScore = computed<Score>(() => ({ home: event.value?.homeTeam.liveScore ?? 0, away: event.value?.awayTeam.liveScore ?? 0 }));
    const { displayScore } = useLiveScoreDisplay(event, providerLiveScore, sboLiveScore, SiteStyle.Euro, ref(true));
    const { isShowCountdown, isShowCountdownHours, countdownDisplay } = useEPenaltyEventCountdown(event);

    const liveScoreItems = computed(() => [
        {
            name: 'set',
            isShow: displayScore.value && displayScore.value.setScore && !isShowCountdown.value,
            homeScore: displayScore.value?.setScore?.home,
            awayScore: displayScore.value?.setScore?.away,
            isSetScore: true,
        },
        {
            name: 'score',
            isShow: displayScore.value && displayScore.value.score && !isShowCountdown.value,
            homeScore: displayScore.value?.score?.home,
            awayScore: displayScore.value?.score?.away,
            isSetScore: false,
        },
    ]);

    const liveTimeDisplay = computed(() => {
        if (!event.value?.isLive) return null;
        if (!displayScore.value) return i18n.t('live');
        if (displayScore.value.cricketDisplayText) return displayScore.value.cricketDisplayText;
        return displayScore.value.setDisplayText ? displayScore.value.setDisplayText : displayScore.value.periodText;
    });

    const showTimeDisplay = computed(() => {
        if (!event.value) return null;
        const displayDateTime = useShowTimeDisplay(event.value.showTime, event.value.showTimeType);
        return `${displayDateTime.date} ${displayDateTime.time}`;
    });

    const { isJerseyVisible } = useJersey();

    const isShowJersey = computed(() => {
        if (!event.value) {
            return false;
        }

        if (!isJerseyVisible(event.value.eventCode, event.value.sportType, event.value.league.size)) {
            return false;
        }

        if (event.value.homeTeam.jersey.type <= 0 || event.value.awayTeam.jersey.type <= 0) {
            return false;
        }

        return true;
    });

    const [displayPreviewEvent, setDisplayPreviewEvent] = useState(event.value);

    const { isLiveStreamPlayable, openLiveStream: _openLiveStream, streamDisplay } = useLiveStream(displayPreviewEvent);
    const { isLiveCourtAvailable, openLiveCourt: _openLiveCourt } = useLiveCourt(displayPreviewEvent);

    const [matchDetailPreview, _setMatchDetailPreview] = useState(MatchDetailPreview.None);
    function toggleMatchDetailPreview(newMatchDetailPreview: MatchDetailPreview) {
        if (newMatchDetailPreview === MatchDetailPreview.LiveStream && !isLiveStreamPlayable.value) {
            return;
        }

        if (newMatchDetailPreview === matchDetailPreview.value) {
            _setMatchDetailPreview(MatchDetailPreview.None);
        } else {
            _setMatchDetailPreview(newMatchDetailPreview);
        }
    }

    function openLiveStream() {
        _openLiveStream(displayPreviewEvent.value?.id);
        _setMatchDetailPreview(MatchDetailPreview.None);
    }

    function openLiveCourt() {
        _openLiveCourt();
        _setMatchDetailPreview(MatchDetailPreview.None);
    }

    watch(event, () => {
        if (event.value) {
            setDisplayPreviewEvent(event.value);
        } else if (streamDisplay.value?.provider === LiveStreamProvider.BetGenius) {
            // when live stream provider is bet genius, need to close the stream when match is closed
            setDisplayPreviewEvent(null);
            _setMatchDetailPreview(MatchDetailPreview.None);
        }
    });

    const route = useRoute();
    once(() => !!displayPreviewEvent.value, () => {
        if (route.query.watch) {
            if (route.query.watch === MatchDetailPreview.LiveStream && isLiveStreamPlayable.value) {
                toggleMatchDetailPreview(MatchDetailPreview.LiveStream);
            }
            if (route.query.watch === MatchDetailPreview.LiveCourt && isLiveCourtAvailable.value) {
                toggleMatchDetailPreview(MatchDetailPreview.LiveCourt);
            }
        }
    });
</script>

<style lang="scss" scoped>
@import "@/components/euroDisplay/MatchDetailHeader";
</style>
